import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Alert,
  AutoComplete,
  Typography,
  Modal,
  message,
  Switch
} from "antd";
import UserService from "../utils/api/users";

const { Text } = Typography;

export default function AddUsers(props) {
  const { isEditMode = false, userData = null, open = false, onSuccess, onCancel } = props;
  const [form] = Form.useForm();
  const [load, setLoad] = useState(false);
  const [error, setError] = useState({ msjError: false, msg: "" });
  const [emailOptions, setAutoCompleteResult] = useState([]);
  const [userStatus, setUserStatus] = useState(true);
  const [supplierCategory, setSupplierCategory] = useState(null);
  const [loadingSupplier, setLoadingSupplier] = useState(false);

  const categoryMap = useMemo(() => ({
    1: "MRO",
    2: "Empaque",
    3: "MRP",
    4: "Servicios",
    5: "Maquinados",
    6: "Tecnología"
  }), []);

  const userDataLocal = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (open && isEditMode && userData) {
      form.setFieldsValue({
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        role_id: userData.role?.toString(),
        status: userData.status
      });
      setUserStatus(userData.status);

      // Cargar categoría del proveedor si el rol es 6
      if (userData.role?.toString() === "6" && userData.id) {
        setLoadingSupplier(true);
        UserService.GetSupplierInfo(userData.id)
          .then((response) => {
            const data = response?.data || response;
            const cat = data?.category ?? data?.categoria ?? null;
            setSupplierCategory(cat);
          })
          .catch(() => {
            setSupplierCategory(null);
          })
          .finally(() => {
            setLoadingSupplier(false);
          });
      } else {
        setSupplierCategory(null);
      }
    } else if (!open) {
      form.resetFields();
      setError({ msjError: false, msg: "" });
      setUserStatus(true);
      setSupplierCategory(null);
    }
  }, [open, isEditMode, userData, form]);

  const onEmailChange = useCallback((value) => {
    if (!value) {
      setAutoCompleteResult([]);
    } else {
      setAutoCompleteResult(
        ["gmail.com", "outlook.com", "hotmail.com", "yahoo.com"].map(
          (domain) => ({
            label: `${value}@${domain}`,
            value: `${value}@${domain}`,
          })
        )
      );
    }
  }, []);

  const handleStatusChange = useCallback((checked) => {
    setUserStatus(checked);
    form.setFieldsValue({ status: checked });
  }, [form]);

  const onFinish = useCallback(async (values) => {
    setLoad(true);
    setError({ msjError: false, msg: "" });

    try {
      let response;

      if (isEditMode && userData) {
        const updateData = {
          userId: userData.id,
          name: values.name,
          phone: values.phone,
          status: userStatus
        };

        response = await UserService.UpdateUser(updateData);

      } else {
        const user = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          bussiness: userDataLocal.bussiness,
          role: values.role_id,
          password: values.password,
          active: true,
          status: true
        };

        response = await UserService.RegisterAdmin(user);
      }

      if (response.data && (response.data.success === true || response.status === 200)) {
        message.success(isEditMode ? "Usuario actualizado correctamente" : "Usuario creado correctamente");
        if (onSuccess) {
          onSuccess();
        }
        handleCancel();
      } else {
        setError({
          msjError: true,
          msg: response.data?.message || `Error al ${isEditMode ? "actualizar" : "crear"} el usuario`,
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error ||
        error.response?.data?.message ||
        `Hubo un error al ${isEditMode ? "actualizar" : "crear"} el usuario`;

      setError({
        msjError: true,
        msg: errorMsg,
      });
    } finally {
      setLoad(false);
    }
  }, [isEditMode, userData, userStatus, userDataLocal, onSuccess]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const modalFooter = [
    <Button key="cancel" onClick={handleCancel} disabled={load}>
      Cancelar
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={load}
      onClick={() => form.submit()}
    >
      {isEditMode ? "Actualizar" : "Registrar"}
    </Button>,
  ];

  return (
    <Modal
      title={isEditMode ? "Editar Usuario" : "Agregar Nuevo Usuario"}
      open={open}
      onCancel={handleCancel}
      footer={modalFooter}
      width={700}
      centered
      styles={{
        content: {
          padding: 0,
          margin: 0,
          borderRadius: '16px',
        },
        header: {
          padding: '20px 24px',
          margin: 0,
          background: '#0f5197',
          borderBottom: 'none',
          borderRadius: '16px 16px 0 0',
        },
        body: {
          padding: '24px',
          margin: 0,
        },
        footer: {
          padding: '16px 24px',
          margin: 0,
          borderTop: '1px solid #f0f0f0',
          background: '#fafafa',
          borderRadius: '0 0 16px 16px',
        }
      }}
    >
      {error.msjError && (
        <Alert
          message={error.msg}
          type="error"
          closable
          showIcon
          style={{ marginBottom: 24 }}
          onClose={() => setError({ msjError: false, msg: "" })}
        />
      )}

      {!isEditMode && (
        <Alert
          message="Información importante"
          description="El usuario agregado quedará asignado a tu misma organización."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Nombre completo"
              rules={[
                { required: true, message: "Ingresa el nombre de usuario" },
              ]}
            >
              <Input
                size="large"
                placeholder="Ej: Juan Pérez García"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="email"
              label="Correo electrónico"
              rules={[
                { type: "email", message: "El correo no es válido" },
                { required: true, message: "Ingresa un correo electrónico" },
              ]}
            >
              <AutoComplete
                options={emailOptions}
                onChange={onEmailChange}
                disabled={isEditMode}
              >
                <Input
                  size="large"
                  placeholder="usuario@ejemplo.com"
                />
              </AutoComplete>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="phone"
              label="Teléfono"
              rules={[
                { required: true, message: "Agrega un número de teléfono" },
                { pattern: /^(?:\d*)$/, message: "Solo se permiten números" },
                { pattern: /^[\d]{10,13}$/, message: "El teléfono debe tener entre 10 y 13 dígitos" },
              ]}
            >
              <Input
                size="large"
                placeholder="Ej: 5512345678"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="role_id"
              label="Tipo de usuario"
              rules={[{ required: true, message: "Selecciona un tipo de usuario" }]}
            >
              {isEditMode ? (
                <Select
                  size="large"
                  placeholder="Selecciona el tipo"
                  disabled
                >
                  <Select.Option value="1">Administrador</Select.Option>
                  <Select.Option value="2">Finanzas internas</Select.Option>
                  <Select.Option value="3">Comprador</Select.Option>
                  <Select.Option value="4">Cliente</Select.Option>
                  <Select.Option value="5">Vendedor</Select.Option>
                  <Select.Option value="6">Proveedor</Select.Option>
                  <Select.Option value="9">Repartidor</Select.Option>
                </Select>
              ) : (
                <Select
                  size="large"
                  placeholder="Selecciona el tipo"
                >
                  <Select.Option value="3">Comprador</Select.Option>
                  <Select.Option value="5">Vendedor</Select.Option>
                </Select>
              )}
            </Form.Item>
          </Col>

          {isEditMode && userData?.role?.toString() === "6" && (
            <Col span={12}>
              <Form.Item
                label="Categoría del proveedor"
              >
                <Select
                  size="large"
                  placeholder={loadingSupplier ? "Cargando..." : "Sin categoría"}
                  value={supplierCategory != null ? supplierCategory : undefined}
                  disabled
                  loading={loadingSupplier}
                >
                  <Select.Option value={1}>MRO</Select.Option>
                  <Select.Option value={2}>Empaque</Select.Option>
                  <Select.Option value={3}>MRP</Select.Option>
                  <Select.Option value={4}>Servicios</Select.Option>
                  <Select.Option value={5}>Maquinados</Select.Option>
                  <Select.Option value={6}>Tecnología</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          )}

          {!isEditMode && (
            <>
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="Contraseña"
                  rules={[
                    { required: true, message: "La contraseña es obligatoria" },
                    { min: 8, message: "Mínimo 8 caracteres" },
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    size="large"
                    placeholder="Mínimo 8 caracteres"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="confirm"
                  label="Confirmar contraseña"
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    { required: true, message: "Confirma la contraseña" },
                    ({ getFieldValue }) => ({
                      validator(rule, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject("Las contraseñas no coinciden");
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="Repite la contraseña"
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {isEditMode && (
            <Col span={24}>
              <Form.Item
                name="status"
                label="Estado"
                valuePropName="checked"
              >
                <Row align="middle" gutter={16}>
                  <Col>
                    <Switch
                      checked={userStatus}
                      onChange={handleStatusChange}
                      checkedChildren="Activo"
                      unCheckedChildren="Inactivo"
                      style={{
                        backgroundColor: userStatus ? '#52c41a' : '#ff4d4f',
                      }}
                    />
                  </Col>
                  <Col>
                    <Text type={userStatus ? "success" : "danger"}>
                      {userStatus ? "Usuario activo" : "Usuario inactivo"}
                    </Text>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
}